import { Injectable } from '@angular/core';
import { HttpClient, HttpContext, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';

import { SKIP_ERROR_TOAST } from '../../../shared/http-context-tokens';
import { SdsPagedResponse } from './sds-api.service';
import {
  SdsListQrCode,
  SdsListQrCodeCreateRequest,
  SdsPublicListContext,
  SdsPublicListQuery,
  SdsPublicListRow
} from '../models/sds-public.model';

/**
 * Two groups of calls that share one feature:
 *
 * - the authenticated lifecycle of the chemicals QR code (generate, read, revoke), and
 * - the anonymous list a guest reaches by scanning it.
 *
 * The anonymous calls opt out of the global error toast. A guest whose link has been revoked
 * should see the page's own "no longer available" message, not a red technical toast; and the
 * toast text assumes a logged-in user with someone to contact.
 */
@Injectable({
  providedIn: 'root'
})
export class SdsPublicApiService {
  private readonly host = environment.BASE_URL_V2;
  private readonly qrCodeApiBaseUrl = `${this.host}/safety-data-sheets/qr-code`;
  private readonly publicApiBaseUrl = `${this.host}/public/sds`;

  /** Anonymous requests handle their own failures in the page. */
  private readonly silentContext = new HttpContext().set(SKIP_ERROR_TOAST, true);

  constructor(private http: HttpClient) {}

  // ===== Authenticated: QR code lifecycle =====

  /** Returns the business' live QR code, or null (204) when it has none. */
  getBusinessQrCode(businessId: number): Observable<SdsListQrCode | null> {
    return this.http.get<SdsListQrCode | null>(`${this.qrCodeApiBaseUrl}/${businessId}`);
  }

  /** Generates a QR code, replacing and revoking any existing one. */
  createQrCode(payload: SdsListQrCodeCreateRequest): Observable<SdsListQrCode> {
    return this.http.post<SdsListQrCode>(this.qrCodeApiBaseUrl, payload);
  }

  /** Revokes the business' QR code. Every printed copy of the URL stops working. */
  revokeQrCode(businessId: number): Observable<void> {
    return this.http.delete<void>(`${this.qrCodeApiBaseUrl}/${businessId}`);
  }

  // ===== Anonymous: the public list =====

  /** Opens a scanned token: who the list belongs to, and which filters to offer. */
  getPublicContext(token: string): Observable<SdsPublicListContext> {
    return this.http.get<SdsPublicListContext>(
      `${this.publicApiBaseUrl}/${encodeURIComponent(token)}`,
      { context: this.silentContext }
    );
  }

  /** One page of the business' chemicals, with search, filters and sorting applied. */
  getPublicChemicals(
    token: string,
    query: SdsPublicListQuery
  ): Observable<SdsPagedResponse<SdsPublicListRow>> {
    let params = new HttpParams()
      .set('PageNumber', String(query.pageNumber))
      .set('PageSize', String(query.pageSize))
      .set('SortColumn', query.sortColumn)
      .set('SortDirection', query.sortDirection);

    if (query.search) {
      params = params.set('Search', query.search);
    }
    if (query.pictogram) {
      params = params.set('Pictogram', query.pictogram);
    }
    if (query.manufacturer) {
      params = params.set('Manufacturer', query.manufacturer);
    }
    if (query.risk) {
      params = params.set('Risk', query.risk);
    }

    return this.http.get<SdsPagedResponse<SdsPublicListRow>>(
      `${this.publicApiBaseUrl}/${encodeURIComponent(token)}/chemicals`,
      { params, context: this.silentContext }
    );
  }

  /**
   * The URL a guest opens to read one chemical's safety data sheet.
   * The API streams the file, so no storage URL is ever handed to the browser.
   */
  getPublicChemicalFileUrl(token: string, documentId: number, download = false): string {
    const downloadQuery = download ? '?download=true' : '';

    return `${this.publicApiBaseUrl}/${encodeURIComponent(token)}/chemicals/${documentId}/file${downloadQuery}`;
  }
}
